import {
    FulfillmentContextProviderFromOrder,
    useFulfillmentContext,
} from '@/modules/fulfillment/context/FulfillmentContext.tsx';
import { useCancelOrderMutation } from '@/clients/order/useCancelOrderMutation.ts';
import { useFulfillOrderMutation } from '@/clients/order/useFulfillOrderMutation.ts';
import { ReloadIcon } from '@schuettflix/icons-react';
import { Tag, TagProps } from '@schuettflix/react-components';
import React, { Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrderStatus } from '@/clients/fulfillment/useOrderStatus.ts';
import { useFormatters } from '@/shared/hooks/useFormatters/useFormatters.ts';
import { GetOrderResponse, ORDER_PROCESS_STATUS, ORDER_SOURCE_SCHEMA } from '@schuettflix/interfaces';
import { useMonolithFrontendUrl } from '@/shared/hooks/useMonolithFrontendUrl.ts';
import { encodeBase64Json } from '@/shared/utils/base64Json.ts';
import { BeforeUnloadContext } from '@/shared/context/BeforeUnloadContext.tsx';
import { FulfillmentUploadProvider } from './context/FulfillmentUploadContext';
import { useFulfillmentDiffCheck } from './hooks/useFulfillmentDiffCheck';
import {
    BoxedGroup,
    Button,
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@schuettflix/planum-react';
import { usePlatformOrganizationId } from '@/clients/platformInfo/usePlatformInfo.ts';
import { DirectFulfillmentConfirmation } from './components/DirectFulfillmentConfirmation';
import { FulfillmentBaseFrame } from './components/FulfillmentBaseFrame';
import { FulfillmentBaseContent } from './components/FulfillmentBaseContent';
import { NonEmptyArray } from '@/shared/types/NonEmptyArray';
import {
    FinanceSettingsLocalStateProvider,
    useFinanceSettingsLocalState,
} from '@/shared/components/FinanceSettings/context/FinanceSettingsLocalStateContext';
import { SUPPORTED_UNIT_LABEL } from '@/phrase/SupportedUnitLabels.ts';
import { useSetDocumentsAsBilled } from '@/modules/fulfillment/hooks/useSetDocumentsAsBilled.ts';
import { useConstructionProject } from '@/clients/constructionProject/useConstructionProject.ts';

export const FulfillmentPage: React.FC<{ order: GetOrderResponse }> = ({ order }) => {
    return (
        <FulfillmentBaseFrame>
            <FulfillmentContextProviderFromOrder orderId={order.id}>
                <FulfillmentUploadProvider>
                    <FinanceSettingsLocalStateProvider>
                        <FulfillmentContent />
                    </FinanceSettingsLocalStateProvider>
                </FulfillmentUploadProvider>
            </FulfillmentContextProviderFromOrder>
        </FulfillmentBaseFrame>
    );
};

const orderStatusReason = {
    COMPLETED: 'fulfillment.footer.fulfilled.title',
    CANCELED: 'fulfillment.footer.cancelled.title',
} as const satisfies Record<Exclude<ORDER_PROCESS_STATUS, 'NEW' | 'IN_PROGRESS'>, string>;

const tagLabel = {
    NEW: 'fulfillment.header.tag.new.label',
    IN_PROGRESS: 'fulfillment.header.tag.open.label',
    COMPLETED: 'fulfillment.header.tag.completed.label',
    CANCELED: 'fulfillment.header.tag.canceled.label',
} as const satisfies Record<ORDER_PROCESS_STATUS, string>;

const tagType: Record<ORDER_PROCESS_STATUS, TagProps['type']> = {
    NEW: 'warning',
    IN_PROGRESS: 'warning',
    COMPLETED: 'positive',
    CANCELED: 'error',
};

const FulfillmentContent = () => {
    const {
        products,
        documents,
        errors,
        setSubmitted,
        isPageValid,
        canSubmit,
        getLineItemListingWithProductFallback,
        lineItemListing,
        order,
        position,
        contextSource,
    } = useFulfillmentContext();
    if (contextSource !== 'order')
        throw new Error('The FulfillmentPage expects to have a fulfillmentContext with the contextSource === order');

    const isDirectFulfillment = order.source === ORDER_SOURCE_SCHEMA.Enum.DIRECT_POSITION_FULFILLMENT;

    const { t } = useTranslation();
    const { data: orderStatus } = useOrderStatus(order.id);
    const { data: constructionProject } = useConstructionProject(order.customerInfo?.constructionProjectId);

    const { formatDate, formatNumber } = useFormatters();
    const beforeUnloadContext = useContext(BeforeUnloadContext);

    const { openUrl: redirectToMonolith } = useMonolithFrontendUrl({
        path: `/orders/${order.id}`,
        target: 'currentTab',
        historyMethod: 'push',
        query: useMemo(
            () => ({
                order: encodeBase64Json({ search: order.humanReadableId }),
            }),
            [order.humanReadableId]
        ),
    });

    const platformOrganizationId = usePlatformOrganizationId();
    const setDocumentsAsBilled = useSetDocumentsAsBilled();

    const onFulfillOrderMutationSuccess = useCallback(
        async function handleFulfillOrderMutationSuccess() {
            if (lineItemListing) {
                await setDocumentsAsBilled(lineItemListing, documents);
            }

            if (isDirectFulfillment && position?.id) {
                setShowDirectFulfillmentConfirmation(true);
                return;
            }

            redirectToMonolith({
                toast: {
                    message: t('fulfillment.toast.fulfillmentSuccessful.message'),
                    type: 'success',
                },
                query: {
                    orderForced: encodeBase64Json({ status: ['closed'] }),
                },
            });
        },
        [lineItemListing, isDirectFulfillment, position?.id, setDocumentsAsBilled, documents, redirectToMonolith, t]
    );

    const { mutate: fulfillmentMutation, isPending: fulfillmentMutationIsPending } = useFulfillOrderMutation(order.id, {
        onSuccess: onFulfillOrderMutationSuccess,
    });
    const { mutate: cancelOrderMutation, isPending: cancelOrderMutationIsPending } = useCancelOrderMutation();

    const [orderClosed, setOrderClosed] = useState(false);
    const [showDirectFulfillmentConfirmation, setShowDirectFulfillmentConfirmation] = useState(false);

    const isFulfilled = orderStatus?.orderStatus === 'COMPLETED';
    const isCancelled = orderStatus?.orderStatus === 'CANCELED';
    const isClosed = isFulfilled || isCancelled;

    const closedReason = useMemo(() => {
        if (orderStatus?.closedAt) {
            return t(orderStatusReason[isFulfilled ? 'COMPLETED' : 'CANCELED'], {
                date: formatDate(orderStatus.closedAt),
            });
        }
        return '';
    }, [orderStatus?.closedAt, formatDate, t, isFulfilled]);

    useEffect(() => {
        if (orderStatus?.orderStatus === 'IN_PROGRESS') {
            setOrderClosed(true);
        }
    }, [orderStatus?.orderStatus, setOrderClosed]);

    const { isAnyDiffExceedingThreshold, diffsExceedingThreshold } = useFulfillmentDiffCheck();
    const [isDiffCheckModalOpen, setIsDiffCheckModalOpen] = useState(false);

    const { getPaymentOrganizationsByProductId } = useFinanceSettingsLocalState();

    const fulfillOrder = useCallback(() => {
        beforeUnloadContext.setEnabled(false);
        setSubmitted(true);

        if (!isPageValid) {
            return errors[0]?.scrollToElement();
        }

        if (!isDiffCheckModalOpen && isAnyDiffExceedingThreshold) {
            return setIsDiffCheckModalOpen(true);
        }

        fulfillmentMutation({
            clientInformation: {
                userId: order.customerInfo!.userId,
                manualCostCenter:
                    order.customerInfo?.manualCostCenter ??
                    position?.customerInfo?.manualCostCenter ??
                    constructionProject?.costCenter?.trim() ??
                    undefined,
                projectCode: order.customerInfo!.projectCode,
            },
            products: products
                .filter(product => product.quote !== undefined)
                .map(product => {
                    if (!product.quote) throw new Error(`Product quote for product ${product.id} is missing`);
                    return {
                        id: product.id,
                        quoteId: product.quote.id,
                        name: product.quote.name,
                        amount: product.quote.amount,
                        unit: product.quote.unit,
                        currencyCode: product.quote.currencyCode,
                        partnerOrganizationId: product.quote.partnerOrganizationId,
                        orderingOrganizationId: product.quote.orderingOrganizationId,
                        platformOrganizationId: product.quote.platformOrganizationId,
                        purchasePrice: product.quote.purchasePrice.toNumber(),
                        purchaseTaxClassId: product.quote.purchaseTaxClassId,
                        salesPrice: product.quote.salesPrice.toNumber(),
                        salesTaxClassId: product.quote.salesTaxClassId,
                        serviceDate: new Date(product.quote.serviceDate),
                        paymentOrganizations: getPaymentOrganizationsByProductId(product.id),
                    };
                }),
            supportingDocuments: documents.map(document => ({
                description: 'Invoice Document',
                urlPath: document.url,
                fileName: document.fileName,
            })) as NonEmptyArray<{ description: string; urlPath: string; fileName: string }>,
            lineItemListing: getLineItemListingWithProductFallback().map(
                ({ accompanyingDocumentNumber, licensePlate, referenceDocument, ...rest }) => ({
                    ...rest,
                    accompanyingDocumentNumber:
                        !accompanyingDocumentNumber || !accompanyingDocumentNumber.trim()
                            ? undefined
                            : accompanyingDocumentNumber.trim(),
                    licensePlate: !licensePlate || !licensePlate.trim() ? undefined : licensePlate.trim(),
                    referenceDocument:
                        !referenceDocument || !referenceDocument.trim() ? undefined : referenceDocument.trim(),
                })
            ),
            fulfilledWithIndividualItemListing: !!lineItemListing,
        });
    }, [
        beforeUnloadContext,
        setSubmitted,
        isPageValid,
        isDiffCheckModalOpen,
        isAnyDiffExceedingThreshold,
        fulfillmentMutation,
        order.customerInfo,
        position?.customerInfo?.manualCostCenter,
        constructionProject?.costCenter,
        products,
        documents,
        getLineItemListingWithProductFallback,
        lineItemListing,
        errors,
        getPaymentOrganizationsByProductId,
    ]);

    const cancelOrder = useCallback(() => {
        beforeUnloadContext.setEnabled(false);

        cancelOrderMutation(order.id, {
            onSuccess: () => {
                redirectToMonolith({
                    toast: {
                        message: t('fulfillment.toast.cancelOrderSuccessful.message'),
                        type: 'success',
                    },
                    query: {
                        orderForced: encodeBase64Json({ status: ['canceled'] }),
                    },
                });
            },
        });
    }, [beforeUnloadContext, cancelOrderMutation, order.id, redirectToMonolith, t]);

    if (!order || !platformOrganizationId || order.status === 'DRAFT') {
        return;
    }

    const tagPropsFromStatus = (orderStatus: ORDER_PROCESS_STATUS): TagProps => ({
        label: t(tagLabel[orderStatus]),
        type: tagType[orderStatus],
        subdued: true,
    });

    const getFulfillOrderButtonLabel = () => {
        if (isDirectFulfillment) {
            return t('fulfillment.footer.directFulfillOrderButton.label');
        }
        return t('fulfillment.footer.fulfillOrderButton.label');
    };

    const getPageHeaderLabel = () => {
        if (!isDirectFulfillment) {
            return t('fulfillment.header.title', { orderId: order.humanReadableId });
        }
        if (position?.positionNumber) {
            return t('fulfillment.header.directFulfillment.title', { positionNumber: position.positionNumber });
        }
        return '';
    };

    if (showDirectFulfillmentConfirmation && order.positionId && position?.customerInfo) {
        return (
            <DirectFulfillmentConfirmation
                orderNumber={order.humanReadableId}
                orderId={order.id}
                positionId={order.positionId}
                constructionProjectId={position.customerInfo.constructionProjectId}
            />
        );
    }

    return (
        <>
            <FulfillmentBaseContent
                headerTitle={getPageHeaderLabel()}
                headerAdditionalContent={orderStatus && <Tag {...tagPropsFromStatus(orderStatus.orderStatus)} />}
                footer={
                    <>
                        {!isClosed && (
                            <div className="flex w-full max-w-[720px] items-center justify-between">
                                <Button
                                    variant="tertiary"
                                    loading={cancelOrderMutationIsPending}
                                    disabled={fulfillmentMutationIsPending || orderClosed || !canSubmit}
                                    onClick={cancelOrder}
                                >
                                    <ReloadIcon />
                                    {t('fulfillment.footer.cancelOrderButton.label')}
                                </Button>
                                <Button
                                    variant="primary"
                                    loading={fulfillmentMutationIsPending}
                                    disabled={cancelOrderMutationIsPending || orderClosed || !canSubmit}
                                    onClick={fulfillOrder}
                                    data-test="fulfill-order-button"
                                >
                                    {getFulfillOrderButtonLabel()}
                                </Button>
                            </div>
                        )}

                        {closedReason && <p className="font-copy-lg">{closedReason}</p>}
                    </>
                }
            />
            <Dialog open={isDiffCheckModalOpen} onOpenChange={setIsDiffCheckModalOpen}>
                <DialogContent size="sm">
                    <DialogHeader>
                        <DialogTitle>{t('fulfillment.diffCheck.heading')}</DialogTitle>
                    </DialogHeader>
                    <DialogBody>
                        <div className="grid grid-cols-[1fr_auto_auto] gap-x-4 gap-y-2">
                            <div className="font-copy-md-strong">{t('fulfillment.diffCheck.productNameHeader')}</div>
                            <div className="font-copy-md-strong">{t('fulfillment.diffCheck.originalAmountHeader')}</div>
                            <div className="font-copy-md-strong">{t('fulfillment.diffCheck.newAmountHeader')}</div>
                            {diffsExceedingThreshold.map(diffData => (
                                <Fragment key={diffData.productId}>
                                    <div className="font-copy-md">{diffData.productName}</div>
                                    <div className="font-copy-md">
                                        {formatNumber(diffData.originalAmount)} {t(SUPPORTED_UNIT_LABEL[diffData.unit])}
                                    </div>
                                    <div className="font-copy-md">
                                        {formatNumber(diffData.newAmount)} {t(SUPPORTED_UNIT_LABEL[diffData.unit])}
                                    </div>
                                </Fragment>
                            ))}
                        </div>
                    </DialogBody>
                    <DialogFooter>
                        <BoxedGroup>
                            <DialogClose asChild>
                                <Button variant="secondary" type="button">
                                    {t('fulfillment.diffCheck.declineButtonLabel')}
                                </Button>
                            </DialogClose>
                            <Button
                                variant="primary"
                                loading={fulfillmentMutationIsPending}
                                onClick={() => {
                                    fulfillOrder();
                                    setIsDiffCheckModalOpen(false);
                                }}
                            >
                                {t('fulfillment.diffCheck.acceptButtonLabel')}
                            </Button>
                        </BoxedGroup>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};
